/**
 * Cookieless guest-funnel session id.
 *
 * 90% of Velyq users arrive via the free guest CV-ATS test, then convert. That
 * guest→account claim used to rely on the cross-site `velyq_anon_id` cookie —
 * which Chrome blocks in incognito and is phasing out browser-wide, silently
 * breaking the funnel. We now store the anon session id (minted + returned by
 * `POST /ats/guest-analyze`) in localStorage and echo it on every guest→account
 * call via the `X-Anon-Session` header, with the cookie kept only as a legacy
 * fallback on the backend.
 */
const ANON_SESSION_KEY = "velyq_anon_session";

export function getAnonSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(ANON_SESSION_KEY);
  } catch {
    // private-mode / storage-disabled — degrade to cookie fallback
    return null;
  }
}

export function setAnonSessionId(id: string | null | undefined): void {
  if (typeof window === "undefined" || !id) return;
  try {
    window.localStorage.setItem(ANON_SESSION_KEY, id);
  } catch {
    // quota / private-mode — non-fatal, backend cookie fallback still applies
  }
}

export function clearAnonSessionId(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ANON_SESSION_KEY);
  } catch {
    // ignore
  }
}

/**
 * Header object to spread into a fetch when the anon session id is known.
 * Returns `{}` when unset so callers can spread unconditionally:
 *   headers: { "Content-Type": "application/json", ...anonSessionHeader() }
 */
export function anonSessionHeader(): Record<string, string> {
  const id = getAnonSessionId();
  return id ? { "X-Anon-Session": id } : {};
}
